import React, { useState, useEffect } from "react";
import CustomCard from "../../components/CustomCard";
import CustomDropdown from "../../components/CustomDropdown";
import CustomButton from "../../components/CustomButton";
import Popup from "../../components/Popup";
import "../css/InformationAccount.css";
import dropdownOptions from "../../data/dropdownOptions.json";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  createRegulationCompanyMT5Account,
  fetchAndStoreServerPublicKey,
  getApiResponsePayload,
  getResponseCode,
} from "../../utils/apiClient";
import AppConstants from "../../utils/appConstants";

export default function UserRegulation() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Form state
  const [taxId, setTaxId] = useState("");
  const [employmentStatus, setEmploymentStatus] = useState(null);
  const [employmentIndustry, setEmploymentIndustry] = useState(null);
  const [educationLevel, setEducationLevel] = useState(null);
  const [sourceOfWealth, setSourceOfWealth] = useState(null);
  const [netWorth, setNetWorth] = useState(null);
  const [annualIncome, setAnnualIncome] = useState(null);
  const [annualDeposit, setAnnualDeposit] = useState(null);
  const [experienceForex, setExperienceForex] = useState(null);
  const [experienceStocks, setExperienceStocks] = useState(null);
  const [experienceFutures, setExperienceFutures] = useState(null);
  const [experienceCFD, setExperienceCFD] = useState(null);
  const [opsPace, setOpsPace] = useState(null);
  const [avgTxAmount, setAvgTxAmount] = useState("");
  const [expectedAnnualActivity, setExpectedAnnualActivity] = useState("");

  const [loading, setLoading] = useState(false);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [errorPopupMessage, setErrorPopupMessage] = useState("");
  const [errors, setErrors] = useState({});

  useEffect(() => {
    // Initialize server public key
    const initializeServerKey = async () => {
      try {
        await fetchAndStoreServerPublicKey();
      } catch (error) {
        // Failed to initialize
      }
    };
    initializeServerKey();

    // Load saved data from sessionStorage if exists
    const savedData = sessionStorage.getItem('user_regulation_data');
    if (savedData) {
      try {
        const data = JSON.parse(savedData);
        if (data.TAX_ID) setTaxId(data.TAX_ID || "");
        if (data.EMPLOYMENT_STATUS !== null && data.EMPLOYMENT_STATUS !== undefined) setEmploymentStatus(data.EMPLOYMENT_STATUS);
        if (data.EMPLOYMENT_INDUSTRY !== null && data.EMPLOYMENT_INDUSTRY !== undefined) setEmploymentIndustry(data.EMPLOYMENT_INDUSTRY);
        if (data.EDUCATION_LEVEL !== null && data.EDUCATION_LEVEL !== undefined) setEducationLevel(data.EDUCATION_LEVEL);
        if (data.SOURCE_OF_WEALTH !== null && data.SOURCE_OF_WEALTH !== undefined) setSourceOfWealth(data.SOURCE_OF_WEALTH);
        if (data.NET_WORTH !== null && data.NET_WORTH !== undefined) setNetWorth(data.NET_WORTH);
        if (data.ANNUAL_INCOME !== null && data.ANNUAL_INCOME !== undefined) setAnnualIncome(data.ANNUAL_INCOME);
        if (data.ANNUAL_DEPOSIT !== null && data.ANNUAL_DEPOSIT !== undefined) setAnnualDeposit(data.ANNUAL_DEPOSIT);
        if (data.EXPERIENCE_FOREX !== null && data.EXPERIENCE_FOREX !== undefined) setExperienceForex(data.EXPERIENCE_FOREX);
        if (data.EXPERIENCE_STOCKS !== null && data.EXPERIENCE_STOCKS !== undefined) setExperienceStocks(data.EXPERIENCE_STOCKS);
        if (data.EXPERIENCE_FUTURES !== null && data.EXPERIENCE_FUTURES !== undefined) setExperienceFutures(data.EXPERIENCE_FUTURES);
        if (data.EXPERIENCE_CFD !== null && data.EXPERIENCE_CFD !== undefined) setExperienceCFD(data.EXPERIENCE_CFD);
        if (data.OPS_PACE) setOpsPace(data.OPS_PACE);
        if (data.AVG_TX_AMOUNT !== null && data.AVG_TX_AMOUNT !== undefined) setAvgTxAmount(String(data.AVG_TX_AMOUNT));
        if (data.EXPECTED_ANNUAL_ACTIVITY !== null && data.EXPECTED_ANNUAL_ACTIVITY !== undefined) setExpectedAnnualActivity(String(data.EXPECTED_ANNUAL_ACTIVITY));
      } catch (e) {
        // Failed to parse - clear invalid data
        sessionStorage.removeItem('user_regulation_data');
      }
    }
  }, []);

  // Auto-save form data
  useEffect(() => {
    const regulationData = {
      TAX_ID: taxId || null,
      EMPLOYMENT_STATUS: employmentStatus,
      EMPLOYMENT_INDUSTRY: employmentIndustry,
      EDUCATION_LEVEL: educationLevel,
      SOURCE_OF_WEALTH: sourceOfWealth,
      NET_WORTH: netWorth,
      ANNUAL_INCOME: annualIncome,
      ANNUAL_DEPOSIT: annualDeposit,
      EXPERIENCE_FOREX: experienceForex,
      EXPERIENCE_STOCKS: experienceStocks,
      EXPERIENCE_FUTURES: experienceFutures,
      EXPERIENCE_CFD: experienceCFD,
      OPS_PACE: opsPace || null,
      AVG_TX_AMOUNT: avgTxAmount ? parseFloat(avgTxAmount) : null,
      EXPECTED_ANNUAL_ACTIVITY: expectedAnnualActivity ? parseFloat(expectedAnnualActivity) : null
    };
    sessionStorage.setItem('user_regulation_data', JSON.stringify(regulationData));
  }, [taxId, employmentStatus, employmentIndustry, educationLevel, sourceOfWealth, netWorth, annualIncome, annualDeposit, experienceForex, experienceStocks, experienceFutures, experienceCFD, opsPace, avgTxAmount, expectedAnnualActivity]);

  const handleNext = async () => {
    // Validation
    const newErrors = {};

    if (!taxId || taxId.trim() === "") {
      newErrors.taxId = t("userRegulation.taxId.error") || "Tax ID is required";
    }
    if (employmentStatus === null || employmentStatus === undefined) {
      newErrors.employmentStatus = t("userRegulation.employmentStatus.error") || "Employment Status is required";
    }
    if (employmentIndustry === null || employmentIndustry === undefined) {
      newErrors.employmentIndustry = t("userRegulation.employmentIndustry.error") || "Employment Industry is required";
    }
    if (educationLevel === null || educationLevel === undefined) {
      newErrors.educationLevel = t("userRegulation.educationLevel.error") || "Education Level is required";
    }
    if (sourceOfWealth === null || sourceOfWealth === undefined) {
      newErrors.sourceOfWealth = t("userRegulation.sourceOfWealth.error") || "Source of Wealth is required";
    }
    if (netWorth === null || netWorth === undefined) {
      newErrors.netWorth = t("userRegulation.netWorth.error") || "Net Worth is required";
    }
    if (annualIncome === null || annualIncome === undefined) {
      newErrors.annualIncome = t("userRegulation.annualIncome.error") || "Annual Income is required";
    }
    if (annualDeposit === null || annualDeposit === undefined) {
      newErrors.annualDeposit = t("userRegulation.annualDeposit.error") || "Annual Deposit is required";
    }
    if (experienceForex === null || experienceForex === undefined) {
      newErrors.experienceForex = t("userRegulation.experienceForex.error") || "Forex Experience is required";
    }
    if (experienceStocks === null || experienceStocks === undefined) {
      newErrors.experienceStocks = t("userRegulation.experienceStocks.error") || "Stocks Experience is required";
    }
    if (experienceFutures === null || experienceFutures === undefined) {
      newErrors.experienceFutures = t("userRegulation.experienceFutures.error") || "Futures Experience is required";
    }
    if (experienceCFD === null || experienceCFD === undefined) {
      newErrors.experienceCFD = t("userRegulation.experienceCFD.error") || "CFD Experience is required";
    }
    if (!opsPace || opsPace.trim() === "") {
      newErrors.opsPace = t("userRegulation.opsPace.error") || "Operations Pace is required";
    }
    if (!avgTxAmount || avgTxAmount.trim() === "" || isNaN(parseFloat(avgTxAmount)) || parseFloat(avgTxAmount) <= 0) {
      newErrors.avgTxAmount = t("userRegulation.avgTxAmount.error") || "Average Transaction Amount is required and must be a positive number";
    }
    if (!expectedAnnualActivity || expectedAnnualActivity.trim() === "" || isNaN(parseFloat(expectedAnnualActivity)) || parseFloat(expectedAnnualActivity) <= 0) {
      newErrors.expectedAnnualActivity = t("userRegulation.expectedAnnualActivity.error") || "Expected Annual Activity is required and must be a positive number";
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      return;
    }

    // Get email - Priority 1: from localStorage (saved after successful registration)
    let email = localStorage.getItem('registrationEmail');
    
    // Priority 2: Get email from registration_step2_data (if still available)
    if (!email) {
      const savedStep2Data = sessionStorage.getItem('registration_step2_data');
      if (savedStep2Data) {
        try {
          const step2Data = JSON.parse(savedStep2Data);
          email = step2Data.email || null;
        } catch (e) {
          // Failed to parse
        }
      }
    }
    
    // Priority 3: Fallback to userEmail in localStorage (saved during login)
    if (!email) {
      email = localStorage.getItem("userEmail");
    }
    
    if (!email) {
      setErrorPopupMessage(t("userRegulation.errors.emailRequired") || "Email is required. Please complete registration first.");
      setShowErrorPopup(true);
      return;
    }

    // Prepare user regulation data
    const userRegulation = {
      EMAIL: email,
      USER_REGULATION: {
        TAX_ID: taxId.trim(),
        EMPLOYMENT_STATUS: employmentStatus,
        EMPLOYMENT_INDUSTRY: employmentIndustry,
        EDUCATION_LEVEL: educationLevel,
        SOURCE_OF_WEALTH: sourceOfWealth,
        NET_WORTH: netWorth,
        ANNUAL_INCOME: annualIncome,
        ANNUAL_DEPOSIT: annualDeposit,
        EXPERIENCE_FOREX: experienceForex,
        EXPERIENCE_STOCKS: experienceStocks,
        EXPERIENCE_FUTURES: experienceFutures,
        EXPERIENCE_CFD: experienceCFD,
        OPS_PACE: opsPace,
        AVG_TX_AMOUNT: parseFloat(avgTxAmount),
        EXPECTED_ANNUAL_ACTIVITY: parseFloat(expectedAnnualActivity)
      }
    };

    setLoading(true);

    try {
      const response = await createRegulationCompanyMT5Account(userRegulation);

      // Check HTTP status code first
      if (response.status && response.status >= 400) {
        let errorMessage = t("userRegulation.errors.submitFailed") || "An error occurred while saving user regulation. Please try again.";
        
        if (response.data) {
          const errorData = typeof response.data === 'string' ? { message: response.data } : response.data;
          errorMessage = errorData?.MESSAGE || 
                        errorData?.message || 
                        errorData?.error || 
                        errorData?.ERROR ||
                        errorMessage;
        }
        
        setErrorPopupMessage(errorMessage);
        setShowErrorPopup(true);
        setLoading(false);
        return;
      }

      const payload = getApiResponsePayload(response);
      const responseCode = getResponseCode(response);

      if (responseCode === AppConstants.responseOk) {
        // Success
        // Mark user-regulation as completed
        sessionStorage.setItem('user_regulation_completed', 'true');
        // Clear saved data after successful submission
        sessionStorage.removeItem('user_regulation_data');
        // Navigate to urban info page directly (like InformationAccount navigates to personalInformation)
        navigate("/urban-info");
      } else {
        // Error
        let errorMessage = payload?.MESSAGE ||
                          payload?.message ||
                          payload?.error ||
                          payload?.ERROR ||
                          t("userRegulation.errors.submitFailed") || "Failed to save user regulation. Please try again.";
        
        setErrorPopupMessage(errorMessage);
        setShowErrorPopup(true);
      }
    } catch (error) {
      let errorMessage = t("userRegulation.errors.submitFailed") || "An error occurred while saving user regulation. Please try again.";
      
      if (error.response?.data) {
        try {
          const errorData = error.response.data;
          errorMessage = errorData?.MESSAGE || 
                        errorData?.message || 
                        errorData?.error || 
                        errorData?.ERROR ||
                        errorMessage;
        } catch (e) {
          // Failed to parse error data
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setErrorPopupMessage(errorMessage);
      setShowErrorPopup(true);
    } finally {
      setLoading(false);
    }
  };

  // Clear error when field is changed
  const clearError = (fieldName) => {
    if (errors[fieldName]) {
      setErrors({ ...errors, [fieldName]: null });
    }
  };

  // Map dropdown options with translations
  const employmentStatusOptions = dropdownOptions.employmentStatusOptions.map((opt) => ({
    ...opt,
    label: t(`userRegulation.employmentStatus.options.${opt.value}`) !== `userRegulation.employmentStatus.options.${opt.value}`
      ? t(`userRegulation.employmentStatus.options.${opt.value}`)
      : opt.label,
  }));

  const employmentIndustryOptions = dropdownOptions.employmentIndustryOptions.map((opt) => ({
    ...opt,
    label: t(`userRegulation.employmentIndustry.options.${opt.value}`) !== `userRegulation.employmentIndustry.options.${opt.value}`
      ? t(`userRegulation.employmentIndustry.options.${opt.value}`)
      : opt.label,
  }));

  const educationLevelOptions = dropdownOptions.educationLevelOptions.map((opt) => ({
    ...opt,
    label: t(`userRegulation.educationLevel.options.${opt.value}`) !== `userRegulation.educationLevel.options.${opt.value}`
      ? t(`userRegulation.educationLevel.options.${opt.value}`)
      : opt.label,
  }));

  const sourceOfWealthOptions = dropdownOptions.sourceOfWealthOptions.map((opt) => ({
    ...opt,
    label: t(`userRegulation.sourceOfWealth.options.${opt.value}`) !== `userRegulation.sourceOfWealth.options.${opt.value}`
      ? t(`userRegulation.sourceOfWealth.options.${opt.value}`)
      : opt.label,
  }));

  const netWorthOptions = dropdownOptions.netWorthOptions.map((opt) => ({
    ...opt,
    label: t(`userRegulation.netWorth.options.${opt.value}`) !== `userRegulation.netWorth.options.${opt.value}`
      ? t(`userRegulation.netWorth.options.${opt.value}`)
      : opt.label,
  }));

  const annualIncomeOptions = dropdownOptions.annualIncomeOptions.map((opt) => ({
    ...opt,
    label: t(`userRegulation.annualIncome.options.${opt.value}`) !== `userRegulation.annualIncome.options.${opt.value}`
      ? t(`userRegulation.annualIncome.options.${opt.value}`)
      : opt.label,
  }));

  const annualDepositOptions = dropdownOptions.annualDepositOptions.map((opt) => ({
    ...opt,
    label: t(`userRegulation.annualDeposit.options.${opt.value}`) !== `userRegulation.annualDeposit.options.${opt.value}`
      ? t(`userRegulation.annualDeposit.options.${opt.value}`)
      : opt.label,
  }));

  const experienceOptions = dropdownOptions.experienceOptions.map((opt) => ({
    ...opt,
    label: t(`userRegulation.experience.options.${opt.value}`) !== `userRegulation.experience.options.${opt.value}`
      ? t(`userRegulation.experience.options.${opt.value}`)
      : opt.label,
  }));

  const opsPaceOptions = dropdownOptions.opsPaceOptions.map((opt) => ({
    ...opt,
    label: t(`userRegulation.opsPace.options.${opt.value}`) !== `userRegulation.opsPace.options.${opt.value}`
      ? t(`userRegulation.opsPace.options.${opt.value}`)
      : opt.label,
  }));

  return (
    <div className="page-container">
      <CustomCard>
        <div className="signup-content">
          <h1 className="signup-title">{t("userRegulation.title") || "User Regulation"}</h1>

          {/* Progress - Only step 1 active */}
          <div className="progress-indicator">
            <div className="progress-circle active">
              {t("userRegulation.progress.step1")}
            </div>
            <div className="progress-line" />
            <div className="progress-circle">
              {t("userRegulation.progress.step2")}
            </div>
            <div className="progress-line" />
            <div className="progress-circle">
              {t("userRegulation.progress.step3")}
            </div>
          </div>

          {/* Form */}
          <div className="signup-form user-regulation-form">
            <div className="form-row">
              <div className="form-group col-12">
                <label>{t("userRegulation.taxId.label") || "Tax ID"}</label>
                <div className="input-wrapper">
                  <span className="input-icon">
                    <i className="fa-solid fa-id-card"></i>
                  </span>
                  <input
                    type="text"
                    placeholder={t("userRegulation.taxId.placeholder") || "Enter your Tax ID"}
                    className={`input-box ${errors.taxId ? "input-error" : ""}`}
                    value={taxId}
                    onChange={(e) => {
                      setTaxId(e.target.value);
                      clearError('taxId');
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group col-6">
                <label>{t("userRegulation.employmentStatus.label") || "Employment Status"}</label>
                <CustomDropdown
                  options={employmentStatusOptions}
                  value={employmentStatusOptions.find(opt => opt.value === employmentStatus)}
                  onChange={(value) => {
                    setEmploymentStatus(value?.value);
                    clearError('employmentStatus');
                  }}
                  placeholder={t("userRegulation.employmentStatus.placeholder") || "Select Employment Status"}
                  required
                  className={errors.employmentStatus ? "dropdown-error" : ""}
                />
              </div>

              <div className="form-group col-6">
                <label>{t("userRegulation.employmentIndustry.label") || "Employment Industry"}</label>
                <CustomDropdown
                  options={employmentIndustryOptions}
                  value={employmentIndustryOptions.find(opt => opt.value === employmentIndustry)}
                  onChange={(value) => {
                    setEmploymentIndustry(value?.value);
                    clearError('employmentIndustry');
                  }}
                  placeholder={t("userRegulation.employmentIndustry.placeholder") || "Select Employment Industry"}
                  required
                  className={errors.employmentIndustry ? "dropdown-error" : ""}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group col-6">
                <label>{t("userRegulation.educationLevel.label") || "Education Level"}</label>
                <CustomDropdown
                  options={educationLevelOptions}
                  value={educationLevelOptions.find(opt => opt.value === educationLevel)}
                  onChange={(value) => {
                    setEducationLevel(value?.value);
                    clearError('educationLevel');
                  }}
                  placeholder={t("userRegulation.educationLevel.placeholder") || "Select Education Level"}
                  required
                  className={errors.educationLevel ? "dropdown-error" : ""}
                />
              </div>

              <div className="form-group col-6">
                <label>{t("userRegulation.sourceOfWealth.label") || "Source of Wealth"}</label>
                <CustomDropdown
                  options={sourceOfWealthOptions}
                  value={sourceOfWealthOptions.find(opt => opt.value === sourceOfWealth)}
                  onChange={(value) => {
                    setSourceOfWealth(value?.value);
                    clearError('sourceOfWealth');
                  }}
                  placeholder={t("userRegulation.sourceOfWealth.placeholder") || "Select Source of Wealth"}
                  required
                  className={errors.sourceOfWealth ? "dropdown-error" : ""}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group col-6">
                <label>{t("userRegulation.netWorth.label") || "Net Worth"}</label>
                <CustomDropdown
                  options={netWorthOptions}
                  value={netWorthOptions.find(opt => opt.value === netWorth)}
                  onChange={(value) => {
                    setNetWorth(value?.value);
                    clearError('netWorth');
                  }}
                  placeholder={t("userRegulation.netWorth.placeholder") || "Select Net Worth"}
                  required
                  className={errors.netWorth ? "dropdown-error" : ""}
                />
              </div>

              <div className="form-group col-6">
                <label>{t("userRegulation.annualIncome.label") || "Annual Income"}</label>
                <CustomDropdown
                  options={annualIncomeOptions}
                  value={annualIncomeOptions.find(opt => opt.value === annualIncome)}
                  onChange={(value) => {
                    setAnnualIncome(value?.value);
                    clearError('annualIncome');
                  }}
                  placeholder={t("userRegulation.annualIncome.placeholder") || "Select Annual Income"}
                  required
                  className={errors.annualIncome ? "dropdown-error" : ""}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group col-6">
                <label>{t("userRegulation.annualDeposit.label") || "Annual Deposit"}</label>
                <CustomDropdown
                  options={annualDepositOptions}
                  value={annualDepositOptions.find(opt => opt.value === annualDeposit)}
                  onChange={(value) => {
                    setAnnualDeposit(value?.value);
                    clearError('annualDeposit');
                  }}
                  placeholder={t("userRegulation.annualDeposit.placeholder") || "Select Annual Deposit"}
                  required
                  className={errors.annualDeposit ? "dropdown-error" : ""}
                />
              </div>

              <div className="form-group col-6">
                <label>{t("userRegulation.experienceForex.label") || "Forex Experience"}</label>
                <CustomDropdown
                  options={experienceOptions}
                  value={experienceOptions.find(opt => opt.value === experienceForex)}
                  onChange={(value) => {
                    setExperienceForex(value?.value);
                    clearError('experienceForex');
                  }}
                  placeholder={t("userRegulation.experienceForex.placeholder") || "Select Forex Experience"}
                  required
                  className={errors.experienceForex ? "dropdown-error" : ""}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group col-6">
                <label>{t("userRegulation.experienceStocks.label") || "Stocks Experience"}</label>
                <CustomDropdown
                  options={experienceOptions}
                  value={experienceOptions.find(opt => opt.value === experienceStocks)}
                  onChange={(value) => {
                    setExperienceStocks(value?.value);
                    clearError('experienceStocks');
                  }}
                  placeholder={t("userRegulation.experienceStocks.placeholder") || "Select Stocks Experience"}
                  required
                  className={errors.experienceStocks ? "dropdown-error" : ""}
                />
              </div>

              <div className="form-group col-6">
                <label>{t("userRegulation.experienceFutures.label") || "Futures Experience"}</label>
                <CustomDropdown
                  options={experienceOptions}
                  value={experienceOptions.find(opt => opt.value === experienceFutures)}
                  onChange={(value) => {
                    setExperienceFutures(value?.value);
                    clearError('experienceFutures');
                  }}
                  placeholder={t("userRegulation.experienceFutures.placeholder") || "Select Futures Experience"}
                  required
                  className={errors.experienceFutures ? "dropdown-error" : ""}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group col-6">
                <label>{t("userRegulation.experienceCFD.label") || "CFD Experience"}</label>
                <CustomDropdown
                  options={experienceOptions}
                  value={experienceOptions.find(opt => opt.value === experienceCFD)}
                  onChange={(value) => {
                    setExperienceCFD(value?.value);
                    clearError('experienceCFD');
                  }}
                  placeholder={t("userRegulation.experienceCFD.placeholder") || "Select CFD Experience"}
                  required
                  className={errors.experienceCFD ? "dropdown-error" : ""}
                />
              </div>

              <div className="form-group col-6">
                <label>{t("userRegulation.opsPace.label") || "Operations Pace"}</label>
                <CustomDropdown
                  options={opsPaceOptions}
                  value={opsPaceOptions.find(opt => opt.value === opsPace)}
                  onChange={(value) => {
                    setOpsPace(value?.value);
                    clearError('opsPace');
                  }}
                  placeholder={t("userRegulation.opsPace.placeholder") || "Select Operations Pace"}
                  required
                  className={errors.opsPace ? "dropdown-error" : ""}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group col-12">
                <label>{t("userRegulation.avgTxAmount.label") || "Average Transaction Amount"}</label>
                <div className="input-wrapper">
                  <span className="input-icon">
                    <i className="fa-solid fa-dollar-sign"></i>
                  </span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder={t("userRegulation.avgTxAmount.placeholder") || "Enter average transaction amount"}
                    className={`input-box ${errors.avgTxAmount ? "input-error" : ""}`}
                    value={avgTxAmount}
                    onChange={(e) => {
                      setAvgTxAmount(e.target.value);
                      clearError('avgTxAmount');
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group col-12">
                <label>{t("userRegulation.expectedAnnualActivity.label") || "Expected Annual Activity"}</label>
                <div className="input-wrapper">
                  <span className="input-icon">
                    <i className="fa-solid fa-chart-line"></i>
                  </span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder={t("userRegulation.expectedAnnualActivity.placeholder") || "Enter expected annual activity"}
                    className={`input-box ${errors.expectedAnnualActivity ? "input-error" : ""}`}
                    value={expectedAnnualActivity}
                    onChange={(e) => {
                      setExpectedAnnualActivity(e.target.value);
                      clearError('expectedAnnualActivity');
                    }}
                  />
                </div>
              </div>
            </div>

            <CustomButton 
              className="next-button" 
              onClick={handleNext}
              disabled={loading}
            >
              {loading ? (t("common.loading") || "Loading...") : (t("userRegulation.nextButton") || "Next")}
            </CustomButton>
          </div>
        </div>
      </CustomCard>

      <Popup
        isOpen={showErrorPopup}
        onClose={() => setShowErrorPopup(false)}
        title="Error"
        message={errorPopupMessage}
        buttonText={t("common.ok") || "OK"}
        icon="fa-circle-exclamation"
      />
    </div>
  );
}
